import { useCallback, useEffect, useState } from 'react'
import {
  getManualSlopeTablesForClub,
  SavedSlopeTable,
} from '../manualSlopeTableService'

import loadingErrorHook from '@common/hooks/loadingErrorHook'

const LOAD_TIMEOUT_MS = 15000

export const LOAD_ERROR_TEXT =
  'Could not load the slope tables. Please try again. If the problem persists, please contact Mapping Industries.'

// Note: loadingErrorHook uses the snackbar, so this hook has to be rendered
// below a SnackbarProvider
export const useManualSlopeTables = (clubId?: string) => {
  const [tables, setTables] = useState<SavedSlopeTable[]>([])
  const [loading, setLoading] = useState(!!clubId)
  const [error, setError] = useState<Error | null>(null)
  const [attempt, setAttempt] = useState(0)

  const retry = useCallback(() => setAttempt((n) => n + 1), [])

  useEffect(() => {
    if (!clubId) {
      setTables([])
      setError(null)
      setLoading(false)
      return
    }

    // Keeps a late callback for a previous club from overwriting the state
    let active = true
    setLoading(true)
    setError(null)

    const unsubscribe = getManualSlopeTablesForClub(
      clubId,
      (data) => {
        if (!active) return
        setTables(data)
        setError(null)
        setLoading(false)
      },
      (err) => {
        if (!active) return
        setTables([])
        setError(err)
        setLoading(false)
      }
    )

    return () => {
      active = false
      unsubscribe()
    }
  }, [clubId, attempt])

  // The cancel callback covers a denied read. It does not cover a listener that
  // never answers at all, which is what a missing database connection looks
  // like, so time out as well
  loadingErrorHook(
    loading,
    () => {
      setError(new Error('Timed out while loading manual slope tables'))
      setLoading(false)
    },
    LOAD_TIMEOUT_MS,
    LOAD_ERROR_TEXT,
    () => ({ clubId })
  )

  return { tables, loading, error, retry }
}
